import React, { useEffect, useState } from "react";
import { FiDownload, FiUser, FiCalendar, FiTag, FiArrowLeft } from "react-icons/fi";
import ApiServices from "../services/apiservices";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

const PostDetail = () => {
  const { postLinkTitle } = useParams();

  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = async () => {
    try {
      const res = await ApiServices.getRequest(`/posts/fetch.php?postLinkTitle=${postLinkTitle}`);

      if (res.success) {
        const apiPost = res.post;

        const formattedPost = {
          title: apiPost.title,
          author: apiPost.author,
          date: new Date(apiPost.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          category: apiPost.category,
          featuredImage: apiPost.featuredImage,
          content: [
            {
              type: "text",
              content: apiPost.introContent,
              isIntro: true,
            },
            apiPost.contentImage1 && {
              type: "image",
              src: apiPost.contentImage1,
            },
            {
              type: "text",
              content: apiPost.mainContent,
            },
            apiPost.contentImage2 && {
              type: "image",
              src: apiPost.contentImage2,
            },
            apiPost.pdfFile && {
              type: "pdf",
              title: "Download Full Document",
              src: apiPost.pdfFile,
            },
          ].filter(Boolean),
        };

        setPostData(formattedPost);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [postLinkTitle]);

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-700 border-t-yellow-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xs font-black uppercase tracking-widest text-gray-400">Loading publication...</p>
        </div>
      </div>
    );
  }

  if (!postData) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-50 p-8 rounded-2xl border border-gray-100 text-center shadow-lg">
          <div className="bg-red-50 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm">
            <FiTag className="text-red-500 text-2xl" />
          </div>
          <h2 className="text-xl font-black text-black uppercase tracking-tight mb-2">Post Not Found</h2>
          <p className="text-gray-500 text-xs font-medium mb-6 leading-relaxed">The publication you are looking for may have been archived.</p>
          <button
            onClick={() => window.history.back()}
            className="w-full bg-black text-white py-3 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-green-700 transition-all shadow-md"
          >
            ← Return to News
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white font-sans overflow-x-hidden">
      {/* Header Section */}
      <div className="bg-[#14532d] border-b border-gray-100 py-10 md:py-14 relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-block bg-yellow-400 text-black px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-3">
            {postData.category}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight uppercase tracking-tight">
          {postData.title}         
           </h2>
          <div className="flex flex-wrap items-center gap-6 text-white font-bold uppercase text-[9px] tracking-[0.15em] border-t border-white/10 pt-4">
            {/* <div className="flex items-center gap-1.5">
              <FiUser className="text-yellow-400 text-xs" />
              <span>{postData.author}</span>
            </div> */}
            <div className="flex items-center gap-1.5">
              <FiCalendar className="text-yellow-400 text-xs" />
              <span>{postData.date}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          
          {/* LEFT SIDE - FEATURED IMAGE (Column span changed from 5 to 4 and image constrained) */}
          <div className="lg:col-span-4 w-full">
            {postData.category === "Tender Notice" ? (
              <img
                src={'/images/tender-notice.jpeg'}
                alt="feature-image"
                className="w-full h-auto max-h-[380px] object-contain rounded-2xl shadow-lg border-4 sm:border-8 border-white ring-1 ring-gray-100 bg-gray-50"
              />
            ) : postData.featuredImage ? (
              <div className="lg:sticky lg:top-20">
                <img
                  src={postData.featuredImage}
                  alt="feature-image"
                  className="w-full h-auto max-h-[380px] object-contain rounded-2xl shadow-lg border-4 sm:border-8 border-white ring-1 ring-gray-100 bg-gray-50"
                />
              </div>
            ) : (
              <div className="bg-gray-100 rounded-2xl aspect-square flex items-center justify-center text-gray-400 font-black uppercase text-[10px]">
                No Image
              </div>
            )}
          </div>

          {/* RIGHT SIDE - CONTENT (Column span changed from 7 to 8 for wider text area) */}
          <div className="lg:col-span-8 flex flex-col min-w-0">
            <div className="space-y-4 md:space-y-6 text-gray-700 leading-relaxed font-normal text-base md:text-lg">
              {postData.content.map((block, index) => {
                switch (block.type) {
                  case "text":
                    return (
                      <div
                        key={index}
                        className={`prose prose-base md:prose-lg max-w-none text-gray-700 leading-relaxed
                          ${block.isIntro ? 'prose-p:first-of-type:first-letter:text-4xl prose-p:first-of-type:first-letter:font-black prose-p:first-of-type:first-letter:text-green-700 prose-p:first-of-type:first-letter:float-left prose-p:first-of-type:first-letter:mr-2' : ''}
                          prose-headings:text-black prose-headings:text-lg prose-strong:text-black prose-strong:font-bold prose-p:mb-4`}
                        dangerouslySetInnerHTML={{ __html: block.content }}
                      />
                    );

                  case "image":
                    return (
                      <div key={index} className="relative rounded-xl overflow-hidden shadow-md border-4 border-gray-50 my-6 max-w-xl mx-auto w-full">
                        <img
                          src={block.src}
                          alt="Content"
                          className="w-full h-auto max-h-[450px] object-contain"
                        />
                      </div>
                    );

                  case "pdf":
                    return (
                      <div key={index} className="bg-black text-white rounded-2xl shadow-xl p-6 sm:p-8 my-6 space-y-4 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-green-700/20 rounded-full -mr-12 -mt-12"></div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.15em] text-yellow-400 mb-1">Document Attachment</p>
                          <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight">Full Publication PDF</h3>
                        </div>
                        <div className="rounded-xl overflow-hidden bg-white">
                          <object
                            width="100%"
                            height="350"
                            data={block.src}
                            type="application/pdf"
                            className="bg-white"
                          />
                        </div>
                        <a
                          href={block.src}
                          download
                          className="flex items-center justify-center gap-2 bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-yellow-400 hover:text-black transition-all font-black uppercase tracking-widest text-[10px] shadow-md"
                        >
                          <FiDownload size={14} />
                          Download Official Document
                        </a>
                      </div>
                    );

                  default:
                    return null;
                }
              })}
            </div>

            {/* Signature / Footer */}
            <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-center">
              <div className="w-10 h-1.5 bg-yellow-400 rounded-full"></div>
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-[0.15em]">BEDC Corporate Communications</p>
            </div>
          </div>
        </div>

        {/* Back Button Section */}
        <div className="mt-8 pt-4">
          <a
            href="/publications"
            className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-green-700 transition-colors"
          >
            <FiArrowLeft size={14} />
            Back to Publications
          </a>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;